import { BYTES_PER_GIGABYTE, BYTES_PER_KILOBYTE, BYTES_PER_MEGABYTE } from '../constants/bytes.constants';
import { EFileUploadError } from '../enum/file.upload.enum';

export type TFileUploadError = EFileUploadError | null;

export function sanitizeFileNamePart(identifier: string | number): string {
  return identifier.toString().replace(/[^a-zA-Z0-9-_.]/g, '-');
}

export function buildPdfFileName(prefix: string, identifier: string | number): string {
  return `${sanitizeFileNamePart(prefix)}-${sanitizeFileNamePart(identifier)}.pdf`;
}

export function isFileSizeExceedingLimit(fileSize: number, allowedBytes: number): boolean {
  return fileSize > allowedBytes;
}

export function isFileTypeAllowed(fileType: string, allowedFileTypes: string[]): boolean {
  return allowedFileTypes.includes(fileType);
}

export function formatSupportedFileTypes(fileTypes: string[]): string {
  const labels = fileTypes.map((fileType) => {
    // an allowed extension such as .csv names its type itself
    if (fileType.startsWith('.')) return fileType.slice(1).toUpperCase();
    switch (fileType) {
      case 'image/jpeg':
        return 'JPG';
      case 'image/png':
        return 'PNG';
      case 'image/gif':
        return 'GIF';
      case 'image/webp':
        return 'WebP';
      case 'application/pdf':
        return 'PDF';
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return 'Word';
      case 'application/vnd.ms-excel':
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return 'Excel';
      case 'image/*':
        return 'image';
      default:
        return fileType;
    }
  });

  const uniqueLabels = labels.filter((label, index) => labels.indexOf(label) === index);

  if (uniqueLabels.length === 0) {
    return '';
  }

  if (uniqueLabels.length === 1) {
    return uniqueLabels[0] ?? '';
  }

  return uniqueLabels.join(', ');
}

/** E.g. "2.4 MB"; the locale picks the decimal separator, e.g. "2,4 MB" in German. */
export function formatFileSize(fileSizeBytes: number, locale: string): string {
  if (fileSizeBytes < BYTES_PER_KILOBYTE) {
    return `${fileSizeBytes} B`;
  }

  const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 1, useGrouping: false });
  if (fileSizeBytes >= BYTES_PER_GIGABYTE) {
    return `${format.format(fileSizeBytes / BYTES_PER_GIGABYTE)} GB`;
  }

  if (fileSizeBytes >= BYTES_PER_MEGABYTE) {
    return `${format.format(fileSizeBytes / BYTES_PER_MEGABYTE)} MB`;
  }

  return `${format.format(fileSizeBytes / BYTES_PER_KILOBYTE)} KB`;
}
